import { Map } from "@avinet/adaptive-ui-maps";
import Zoom from "@avinet/adaptive-ui-maps/control/Zoom";
import ZoomToExtent from "@avinet/adaptive-ui-maps/control/ZoomToExtent";
import WmtsLayer from "@avinet/adaptive-ui-maps/layer/WmtsLayer";
import XyzLayer from "@avinet/adaptive-ui-maps/layer/XyzLayer";
import VectorLayer from "@avinet/adaptive-ui-maps/vector/VectorLayer";
import VectorSource from "@avinet/adaptive-ui-maps/vector/source/VectorSource";
import Feature from "ol/Feature";
import { Icon, Style } from "ol/style";
import { ReactNode, useMemo } from "react";
import { renderToStaticMarkup } from "react-dom/server";

import { MapDestinationIcon } from "../../assets/icons/MapDestinationIcon";
import { SelectedMapDestinationIcon } from "../../assets/icons/SelectedMapDestinationIcon";
import "./MapWrapper.scss";

export const MapWrapper = ({
  features,
  selectedId,
}: {
  children: ReactNode;
  features: Feature[];
  selectedId?: number;
}) => {
  const defaultExtent = useMemo(() => [461314, 8462004, 707752, 8715243], []);
  const dummyExtent = useMemo(() => [1000000, 9000000, 400000, 8300000], []);

  const createCustomStyle = () => {
    return new Style({
      image: new Icon({
        anchor: [0.5, 1],
        src: `data:image/svg+xml;utf8,${encodeURIComponent(
          renderToStaticMarkup(<MapDestinationIcon />)
        )}`,
      }),
    });
  };

  const createSelectedStyle = () => {
    return new Style({
      image: new Icon({
        anchor: [0.5, 1],
        src: `data:image/svg+xml;utf8,${encodeURIComponent(
          renderToStaticMarkup(<SelectedMapDestinationIcon />)
        )}`,
      }),
    });
  };

  const extent = useMemo(() => {
    if (features.length === 0) return [];
    const firstFeature = features[0];
    if (!firstFeature) return [];
    const extent: number[] = [...dummyExtent];
    for (let i = 0; i < features.length; i++) {
      const geometry = features[i].getGeometry();
      if (geometry) {
        const featureExtent = geometry.getExtent();
        extent[0] = Math.min(extent[0], featureExtent[0]);
        extent[1] = Math.min(extent[1], featureExtent[1]);
        extent[2] = Math.max(extent[2], featureExtent[2]);
        extent[3] = Math.max(extent[3], featureExtent[3]);
      }
    }

    const buffer = 10000;
    extent[0] -= buffer;
    extent[1] -= buffer;
    extent[2] += buffer;
    extent[3] += buffer;
    if (extent === dummyExtent) {
      return defaultExtent;
    }
    return extent;
  }, [defaultExtent, features, dummyExtent]);

  return (
    <>
      {extent.length > 0 && (
        <Map
          initialExtent={extent}
          minZoom={6}
          maxZoom={20}
          background="#0B0A12"
        >
          <XyzLayer
            url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
            zIndex={1}
          />

          <WmtsLayer
            url="https://opencache.statkart.no/gatekeeper/gk/gk.open_nib_web_mercator_wmts_v2"
            layer="Nibcache_web_mercator_v2"
            matrixSet="default028mm"
            style="default"
            attributions="&copy; Kartverket"
          />
          <Zoom />
          <ZoomToExtent extent={extent} />
          <VectorLayer zIndex={1} style={createCustomStyle}>
            <VectorSource
              features={features.filter(
                (feature) => feature.getId() !== selectedId
              )}
            />
          </VectorLayer>
          <VectorLayer zIndex={2} style={createSelectedStyle}>
            <VectorSource
              features={features.filter(
                (feature) => feature.getId() === selectedId
              )}
            />
          </VectorLayer>
        </Map>
      )}
    </>
  );
};
